'use client'

import { useState } from 'react'

import { useKeenSlider } from 'keen-slider/react'
import { KeenSliderArrows } from '@/components/keen-slider/KeenSliderPlugins'
import 'keen-slider/keen-slider.min.css'
import GridArchiveItem, { GridArchiveItemProps } from '@/components/grid-archive/GridArchiveItem'

/**
 * --------------------------------------------------------------------------
 * LatestPostsGrid
 * --------------------------------------------------------------------------
 **/
export function LatestPostsGrid({ posts }: { posts: GridArchiveItemProps[]; fullWidth?: boolean }) {
  const [isDisabled, setIsDisabled] = useState(false)

  const [sliderRef, instanceRef] = useKeenSlider({
    initial: 0,
    slides: {
      perView: 1,
      spacing: 24,
    },
    breakpoints: {
      '(min-width: 500px)': {
        slides: {
          perView: 2,
          spacing: 24,
        },
      },
      '(min-width: 1300px)': {
        disabled: true,
      },
    },
    created(s) {
      setIsDisabled(s.options.disabled)
    },
    detailsChanged(s) {
      setIsDisabled(s.options.disabled)
    },
  })

  if (!Array.isArray(posts) || !posts?.length || posts?.length === 0) {
    return null
  }

  const gridClass = `
    [@media(min-width:1300px)]:grid
    [@media(min-width:1300px)]:grid-cols-6

    [@media(min-width:1300px)]:gap-m
  `

  return (
    <>
      <div ref={sliderRef} className={`keen-slider ${gridClass}`}>
        {posts.map((post, index) => {
          const postId = post?.post_id

          if (!postId) {
            return null
          }

          const itemClass = index < 2 ? `col-span-3` : `col-span-2`

          return (
            <div className={`keen-slider__slide ${itemClass}`} key={postId}>
              <GridArchiveItem hit={post as any} size={index < 2 ? 'l' : null} />
            </div>
          )
        })}
      </div>
      <KeenSliderArrows sliderInstance={instanceRef} disabled={isDisabled} />
    </>
  )
}

/**
 * --------------------------------------------------------------------------
 * LatestPostsSlider
 * --------------------------------------------------------------------------
 **/
export function LatestPostsSlider({
  posts,
  fullWidth = true,
}: {
  posts: GridArchiveItemProps[]
  fullWidth?: boolean
}) {
  const [isDisabled, setIsDisabled] = useState(false)

  const [sliderRef, instanceRef] = useKeenSlider({
    initial: 0,
    slides: {
      perView: 1,
      spacing: 24,
    },
    breakpoints: {
      '(min-width: 500px)': {
        slides: {
          perView: 2,
          spacing: 24,
        },
      },
      '(min-width: 770px)': {
        slides: {
          perView: fullWidth ? 3 : 2,
          spacing: 24,
        },
      },
      '(min-width: 1300px)': {
        slides: {
          perView: fullWidth ? 4 : 3,
          spacing: 24,
        },
      },
    },
    created(s) {
      setIsDisabled(s.options.disabled)
    },
    detailsChanged(s) {
      setIsDisabled(s.options.disabled)
    },
  })

  if (!posts || posts.length === 0) {
    return null
  }

  return (
    <div className="w-full">
      <div ref={sliderRef} className={`keen-slider`}>
        {posts.map((post) => {
          const postId = post?.post_id

          if (!postId) {
            return null
          }

          return (
            <div className="keen-slider__slide" key={postId}>
              <GridArchiveItem hit={post} />
            </div>
          )
        })}
      </div>

      <KeenSliderArrows sliderInstance={instanceRef} disabled={isDisabled} />
    </div>
  )
}
